import type { AppLang } from '@/i18n/locales/i18n.factory';
import { globalTypePath, type EventType } from './geo';

export type LocalizedSeoLang = Exclude<AppLang, 'he'>;

export const LOCALIZED_SERVICE_LANGS = ['en', 'fr', 'ru'] as const satisfies readonly LocalizedSeoLang[];
export const GLOBAL_TYPE_LINK_LANGS = ['he', ...LOCALIZED_SERVICE_LANGS] as const satisfies readonly AppLang[];

export const LOCALIZED_GLOBAL_TYPE_PATHS: Record<LocalizedSeoLang, Record<EventType, string>> = {
  en: {
    חתונה: '/en/wedding-dj-israel/',
    חינה: '/en/henna-dj-israel/',
    'בר-מצווה': '/en/bar-bat-mitzvah-dj-israel/',
    אירוע: '/en/event-dj-israel/',
  },
  fr: {
    חתונה: '/fr/dj-mariage-israel/',
    חינה: '/fr/dj-henna-israel/',
    'בר-מצווה': '/fr/dj-bar-bat-mitsva-israel/',
    אירוע: '/fr/dj-evenement-israel/',
  },
  ru: {
    חתונה: '/ru/dj-na-svadbu-v-izraile/',
    חינה: '/ru/dj-na-hinu-v-izraile/',
    'בר-מצווה': '/ru/dj-na-bar-bat-mitzvu-v-izraile/',
    אירוע: '/ru/dj-na-meropriyatie-v-izraile/',
  },
} as const;

export const LEGACY_LOCALIZED_GLOBAL_TYPE_PATHS: Record<LocalizedSeoLang, Record<EventType, string>> = {
  en: {
    חתונה: '/en/dj-for-wedding/',
    חינה: '/en/dj-for-henna/',
    'בר-מצווה': '/en/dj-for-bar-bat-mitzvah/',
    אירוע: '/en/dj-for-event/',
  },
  fr: {
    חתונה: '/fr/dj-mariage/',
    חינה: '/fr/dj-henna/',
    'בר-מצווה': '/fr/dj-bar-bat-mitsva/',
    אירוע: '/fr/dj-evenement/',
  },
  ru: {
    חתונה: '/ru/dj-na-svadbu/',
    חינה: '/ru/dj-na-hennu/',
    'בר-מצווה': '/ru/dj-na-bar-bat-mitzvu/',
    אירוע: '/ru/dj-na-meropriyatie/',
  },
} as const;

export const GLOBAL_TYPE_LINK_LABELS: Record<AppLang, Record<EventType, string>> = {
  he: {
    חתונה: 'די ג׳יי לחתונה',
    חינה: 'די ג׳יי לחינה',
    'בר-מצווה': 'די ג׳יי לבר/בת מצווה',
    אירוע: 'די ג׳יי לאירוע',
  },
  en: {
    חתונה: 'Wedding DJ in Israel',
    חינה: 'Henna DJ in Israel',
    'בר-מצווה': 'Bar/Bat Mitzvah DJ in Israel',
    אירוע: 'Event DJ in Israel',
  },
  fr: {
    חתונה: 'DJ mariage en Israël',
    חינה: 'DJ henné en Israël',
    'בר-מצווה': 'DJ Bar/Bat Mitsva en Israël',
    אירוע: 'DJ événement en Israël',
  },
  ru: {
    חתונה: 'DJ на свадьбу в Израиле',
    חינה: 'DJ на хину в Израиле',
    'בר-מצווה': 'DJ на Бар/Бат Мицву в Израиле',
    אירוע: 'DJ на мероприятие в Израиле',
  },
} as const;

const LOCALIZED_GLOBAL_TYPE_ROUTE_GROUPS = [
  LOCALIZED_GLOBAL_TYPE_PATHS,
  LEGACY_LOCALIZED_GLOBAL_TYPE_PATHS,
] as const;

const normalizeRoutePath = (pathname: string) => {
  const raw = String(pathname || '/').split(/[?#]/)[0] || '/';
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  const trimmed = withLeadingSlash.replace(/\/+$/g, '');
  return trimmed || '/';
};

export function getLocalizedGlobalTypePath(
  lang: AppLang | string,
  type: EventType
): string | null {
  if (lang === 'he') return null;
  const langKey = lang as LocalizedSeoLang;
  return LOCALIZED_GLOBAL_TYPE_PATHS[langKey]?.[type] ?? null;
}

export function getGlobalTypePathForLang(
  lang: AppLang | string,
  type: EventType
): string | null {
  if (lang === 'he') return globalTypePath(type);
  return getLocalizedGlobalTypePath(lang, type);
}

export function getGlobalTypeLabelForLang(
  lang: AppLang | string,
  type: EventType
): string {
  return GLOBAL_TYPE_LINK_LABELS[lang as AppLang]?.[type] ?? GLOBAL_TYPE_LINK_LABELS.he[type];
}

export function resolveLocalizedGlobalTypeRoute(
  pathname: string
): { lang: LocalizedSeoLang; type: EventType } | null {
  const normalized = normalizeRoutePath(pathname);

  for (const lang of LOCALIZED_SERVICE_LANGS) {
    for (const routes of LOCALIZED_GLOBAL_TYPE_ROUTE_GROUPS) {
      const entries = Object.entries(routes[lang]) as Array<[EventType, string]>;
      const match = entries.find(([, path]) => normalizeRoutePath(path) === normalized);

      if (match) {
        return { lang, type: match[0] };
      }
    }
  }

  return null;
}
